import { useEffect, useRef, useState, useCallback } from 'react';
import * as fabric from 'fabric';
import { pdfEngine, type PageDimensions } from '../../core/PDFEngine';
import { useAnnotationStore, type Tool } from '../../stores/annotationStore';

interface PDFPageProps {
  pageNumber: number;
  scale: number;
  rotation: number;
}

export function PDFPage({ pageNumber, scale, rotation }: PDFPageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const annotationCanvasRef = useRef<HTMLCanvasElement>(null);
  const [dimensions, setDimensions] = useState<PageDimensions | null>(null);
  const [renderedScale, setRenderedScale] = useState(scale);
  const [isRendering, setIsRendering] = useState(false);
  const renderIdRef = useRef(0);
  const renderTimeoutRef = useRef<number | undefined>(undefined);

  // ── PDF Rendering ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (renderTimeoutRef.current) {
      window.clearTimeout(renderTimeoutRef.current);
    }

    renderTimeoutRef.current = window.setTimeout(() => {
      const renderId = ++renderIdRef.current;
      setIsRendering(true);

      pdfEngine
        .renderPage(pageNumber, { scale, rotation, canvas })
        .then((dims) => {
          if (renderId === renderIdRef.current) {
            setDimensions(dims);
            setRenderedScale(scale);
            setIsRendering(false);
          }
        })
        .catch((err) => {
          if (renderId === renderIdRef.current) {
            console.error(`Error rendering page ${pageNumber}:`, err);
            setIsRendering(false);
          }
        });
    }, 150);

    return () => {
      if (renderTimeoutRef.current) {
        window.clearTimeout(renderTimeoutRef.current);
      }
      renderIdRef.current++;
    };
  }, [pageNumber, scale, rotation]);

  // ── Fabric.js Annotation Canvas ──
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const { activeTool, strokeColor, strokeWidth, fillColor, fontSize, highlightColor, opacity } = useAnnotationStore();
  const isDrawingShape = useRef(false);
  const startPoint = useRef<{ x: number; y: number } | null>(null);
  const currentShape = useRef<fabric.FabricObject | null>(null);

  const saveState = useCallback(() => {
    if (isDrawingShape.current) return;
    if (fabricCanvasRef.current) {
      const json = JSON.stringify(fabricCanvasRef.current.toJSON());
      useAnnotationStore.getState().saveCanvasState(pageNumber, json);
    }
  }, [pageNumber]);

  // Initialize Fabric canvas
  useEffect(() => {
    if (!annotationCanvasRef.current) return;
    if (!fabricCanvasRef.current) {
      const fCanvas = new fabric.Canvas(annotationCanvasRef.current, {
        isDrawingMode: false,
        selection: true,
      });
      fabricCanvasRef.current = fCanvas;

      fCanvas.on('object:modified', saveState);
      fCanvas.on('path:created', saveState);

      useAnnotationStore.getState().registerCanvas(pageNumber, fCanvas);
    }
    return () => {
      useAnnotationStore.getState().unregisterCanvas(pageNumber);
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, [pageNumber, saveState]);

  // Resize Fabric canvas when dimensions change
  useEffect(() => {
    const fCanvas = fabricCanvasRef.current;
    if (!fCanvas || !dimensions) return;

    fCanvas.setDimensions({
      width: dimensions.originalWidth * renderedScale,
      height: dimensions.originalHeight * renderedScale,
    });
  }, [dimensions, renderedScale]);

  // Helper: get highlight color as rgba
  const getHighlightRGBA = useCallback(() => {
    const colors: Record<string, string> = {
      yellow: 'rgba(255, 255, 0, 0.4)',
      green: 'rgba(0, 255, 100, 0.3)',
      pink: 'rgba(255, 105, 180, 0.3)',
      blue: 'rgba(0, 120, 255, 0.3)',
      orange: 'rgba(255, 165, 0, 0.35)',
    };
    return colors[highlightColor] || colors.yellow;
  }, [highlightColor]);

  // ── Configure Tool Behavior ──
  useEffect(() => {
    const fCanvas = fabricCanvasRef.current;
    if (!fCanvas) return;

    // Reset all modes
    fCanvas.isDrawingMode = false;
    fCanvas.selection = false;
    fCanvas.defaultCursor = 'default';
    fCanvas.hoverCursor = 'default';

    // Remove any custom event listeners before re-adding
    fCanvas.off('mouse:down');
    fCanvas.off('mouse:move');
    fCanvas.off('mouse:up');

    switch (activeTool) {
      case 'select':
        fCanvas.selection = true;
        fCanvas.defaultCursor = 'default';
        fCanvas.hoverCursor = 'move';
        fCanvas.forEachObject((obj) => {
          obj.selectable = true;
          obj.evented = true;
        });
        break;

      case 'hand':
        fCanvas.defaultCursor = 'grab';
        fCanvas.hoverCursor = 'grab';
        fCanvas.forEachObject((obj) => {
          obj.selectable = false;
          obj.evented = false;
        });
        break;

      case 'freehand':
        fCanvas.isDrawingMode = true;
        if (fCanvas.freeDrawingBrush) {
          fCanvas.freeDrawingBrush.color = strokeColor;
          fCanvas.freeDrawingBrush.width = strokeWidth * renderedScale;
        }
        break;

      case 'highlight':
        fCanvas.isDrawingMode = true;
        if (fCanvas.freeDrawingBrush) {
          fCanvas.freeDrawingBrush.color = getHighlightRGBA();
          fCanvas.freeDrawingBrush.width = 20 * renderedScale;
        }
        break;

      case 'eraser':
        fCanvas.defaultCursor = 'crosshair';
        fCanvas.hoverCursor = 'crosshair';
        fCanvas.on('mouse:down', (opt: any) => {
          const target = fCanvas.findTarget(opt.e) as unknown as fabric.FabricObject | undefined;
          if (target) {
            fCanvas.remove(target);
            fCanvas.renderAll();
            saveState();
          }
        });
        break;

      // Shape tools: rectangle, circle, arrow, line
      case 'rectangle':
      case 'circle':
      case 'arrow':
      case 'line':
        fCanvas.defaultCursor = 'crosshair';
        fCanvas.hoverCursor = 'crosshair';
        fCanvas.forEachObject((obj) => {
          obj.selectable = false;
          obj.evented = false;
        });

        fCanvas.on('mouse:down', (opt: any) => {
          isDrawingShape.current = true;
          const pointer = fCanvas.getScenePoint(opt.e);
          startPoint.current = { x: pointer.x, y: pointer.y };

          let shape: fabric.FabricObject;
          if (activeTool === 'rectangle') {
            shape = new fabric.Rect({
              left: pointer.x,
              top: pointer.y,
              width: 0,
              height: 0,
              fill: fillColor === 'transparent' ? '' : fillColor,
              stroke: strokeColor,
              strokeWidth: strokeWidth,
              opacity: opacity,
            });
          } else if (activeTool === 'circle') {
            shape = new fabric.Ellipse({
              left: pointer.x,
              top: pointer.y,
              rx: 0,
              ry: 0,
              fill: fillColor === 'transparent' ? '' : fillColor,
              stroke: strokeColor,
              strokeWidth: strokeWidth,
              opacity: opacity,
            });
          } else {
            // line or arrow
            shape = new fabric.Line([pointer.x, pointer.y, pointer.x, pointer.y], {
              stroke: strokeColor,
              strokeWidth: strokeWidth,
              opacity: opacity,
            });
          }
          currentShape.current = shape;
          fCanvas.add(shape);
        });

        fCanvas.on('mouse:move', (opt: any) => {
          if (!isDrawingShape.current || !startPoint.current || !currentShape.current) return;
          const pointer = fCanvas.getScenePoint(opt.e);
          const sx = startPoint.current.x;
          const sy = startPoint.current.y;

          if (activeTool === 'rectangle') {
            const rect = currentShape.current as fabric.Rect;
            rect.set({
              left: Math.min(sx, pointer.x),
              top: Math.min(sy, pointer.y),
              width: Math.abs(pointer.x - sx),
              height: Math.abs(pointer.y - sy),
            });
          } else if (activeTool === 'circle') {
            const ellipse = currentShape.current as fabric.Ellipse;
            ellipse.set({
              left: Math.min(sx, pointer.x),
              top: Math.min(sy, pointer.y),
              rx: Math.abs(pointer.x - sx) / 2,
              ry: Math.abs(pointer.y - sy) / 2,
            });
          } else {
            const line = currentShape.current as fabric.Line;
            line.set({ x2: pointer.x, y2: pointer.y });
          }
          fCanvas.renderAll();
        });

        fCanvas.on('mouse:up', () => {
          if (currentShape.current) {
            currentShape.current.selectable = true;
            currentShape.current.evented = true;

            // Add arrowhead for arrow tool
            if (activeTool === 'arrow' && currentShape.current instanceof fabric.Line) {
              const line = currentShape.current;
              const x1 = line.x1!, y1 = line.y1!, x2 = line.x2!, y2 = line.y2!;
              const angle = Math.atan2(y2 - y1, x2 - x1);
              const headLen = 15;

              const arrowHead = new fabric.Polygon(
                [
                  { x: x2, y: y2 },
                  {
                    x: x2 - headLen * Math.cos(angle - Math.PI / 6),
                    y: y2 - headLen * Math.sin(angle - Math.PI / 6),
                  },
                  {
                    x: x2 - headLen * Math.cos(angle + Math.PI / 6),
                    y: y2 - headLen * Math.sin(angle + Math.PI / 6),
                  },
                ],
                { fill: strokeColor, stroke: strokeColor, strokeWidth: 1 }
              );
              fCanvas.add(arrowHead);
            }
          }
          isDrawingShape.current = false;
          startPoint.current = null;
          currentShape.current = null;

          saveState();
        });
        break;

      // Text Annotation / Sticky Note
      case 'text-annotation':
      case 'sticky-note':
        fCanvas.defaultCursor = 'text';
        fCanvas.hoverCursor = 'text';
        fCanvas.on('mouse:down', (opt: any) => {
          const pointer = fCanvas.getScenePoint(opt.e);
          // Don't add text if clicking on existing object
          const target = fCanvas.findTarget(opt.e);
          if (target) return;

          if (activeTool === 'text-annotation') {
            const text = new fabric.IText('Type here...', {
              left: pointer.x,
              top: pointer.y,
              fontSize: fontSize * renderedScale,
              fill: strokeColor,
              fontFamily: 'Inter, sans-serif',
              editable: true,
            });
            fCanvas.add(text);
            fCanvas.setActiveObject(text);
            text.enterEditing();
          } else {
            // sticky note
            const noteWidth = 180 * renderedScale;
            const noteHeight = 120 * renderedScale;
            const bg = new fabric.Rect({
              left: pointer.x,
              top: pointer.y,
              width: noteWidth,
              height: noteHeight,
              fill: '#fef08a',
              stroke: '#eab308',
              strokeWidth: 1,
              rx: 4,
              ry: 4,
              shadow: new fabric.Shadow({ color: 'rgba(0,0,0,0.15)', blur: 6, offsetX: 2, offsetY: 2 }),
            });
            const txt = new fabric.IText('Note...', {
              left: pointer.x + 8,
              top: pointer.y + 8,
              fontSize: 13 * renderedScale,
              fill: '#713f12',
              fontFamily: 'Inter, sans-serif',
              editable: true,
              width: noteWidth - 16,
            });
            const group = new fabric.Group([bg, txt], {
              left: pointer.x,
              top: pointer.y,
              subTargetCheck: true,
            });
            fCanvas.add(group);
            fCanvas.setActiveObject(group);
          }
          saveState();
        });
        break;

      case 'stamp':
        fCanvas.defaultCursor = 'crosshair';
        fCanvas.on('mouse:down', (opt: any) => {
          const pointer = fCanvas.getScenePoint(opt.e);
          const target = fCanvas.findTarget(opt.e);
          if (target) return;

          const stamp = new fabric.IText('APPROVED', {
            left: pointer.x,
            top: pointer.y,
            fontSize: 28 * renderedScale,
            fill: 'red',
            fontFamily: 'Impact, sans-serif',
            fontWeight: 'bold',
            angle: -15,
            opacity: 0.6,
            editable: true,
          });
          fCanvas.add(stamp);
          fCanvas.setActiveObject(stamp);
          saveState();
        });
        break;

      case 'redact':
        fCanvas.defaultCursor = 'crosshair';
        fCanvas.hoverCursor = 'crosshair';
        fCanvas.on('mouse:down', (opt: any) => {
          isDrawingShape.current = true;
          const pointer = fCanvas.getScenePoint(opt.e);
          startPoint.current = { x: pointer.x, y: pointer.y };
          const rect = new fabric.Rect({
            left: pointer.x,
            top: pointer.y,
            width: 0,
            height: 0,
            fill: 'black',
            stroke: 'black',
            strokeWidth: 0,
            opacity: 1,
          });
          currentShape.current = rect;
          fCanvas.add(rect);
        });
        fCanvas.on('mouse:move', (opt: any) => {
          if (!isDrawingShape.current || !startPoint.current || !currentShape.current) return;
          const pointer = fCanvas.getScenePoint(opt.e);
          const rect = currentShape.current as fabric.Rect;
          rect.set({
            left: Math.min(startPoint.current.x, pointer.x),
            top: Math.min(startPoint.current.y, pointer.y),
            width: Math.abs(pointer.x - startPoint.current.x),
            height: Math.abs(pointer.y - startPoint.current.y),
          });
          fCanvas.renderAll();
        });
        fCanvas.on('mouse:up', () => {
          isDrawingShape.current = false;
          startPoint.current = null;
          currentShape.current = null;
          saveState();
        });
        break;

      default:
        // text-select, underline, strikethrough, measure, signature — keep defaults
        fCanvas.defaultCursor = 'crosshair';
        break;
    }

    return () => {
      fCanvas.off('mouse:down');
      fCanvas.off('mouse:move');
      fCanvas.off('mouse:up');
    };
  }, [activeTool, renderedScale, strokeColor, strokeWidth, fillColor, fontSize, highlightColor, opacity, getHighlightRGBA, saveState]);

  const cssScale = renderedScale > 0 ? scale / renderedScale : 1;
  const targetWidth = dimensions ? dimensions.originalWidth * scale : undefined;
  const targetHeight = dimensions ? dimensions.originalHeight * scale : undefined;
  const renderedWidth = dimensions ? dimensions.originalWidth * renderedScale : undefined;
  const renderedHeight = dimensions ? dimensions.originalHeight * renderedScale : undefined;

  return (
    <div
      className="pdf-page-wrapper"
      data-page={pageNumber}
      style={{
        width: targetWidth,
        height: targetHeight,
      }}
    >
      <div
        style={{
          transform: `scale(${cssScale})`,
          transformOrigin: 'top left',
          width: renderedWidth,
          height: renderedHeight,
        }}
      >
        <canvas ref={canvasRef} className="pdf-page-canvas" />
        <canvas
          ref={annotationCanvasRef}
          className="pdf-annotation-canvas"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />
      </div>
      <div className="pdf-page-number">{pageNumber}</div>
      {isRendering && dimensions && (
        <div
          className="skeleton"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
            opacity: 0.2,
          }}
        />
      )}
    </div>
  );
}
